import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { BsSearch, BsMic, BsSliders, BsMap, BsLightning, BsStars } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import './AdvancedSearchShowcase.css';

const AdvancedSearchShowcase: React.FC = () => {
  const navigate = useNavigate();

  const searchMethods = [
    {
      icon: <BsSearch />,
      title: 'Natural Language',
      description: 'Type like you talk',
      example: '"3 bedroom home with pool near good schools"'
    },
    {
      icon: <BsMic />,
      title: 'Voice Search',
      description: 'Just speak your needs',
      example: '"Show me homes under 500k in Scottsdale"'
    },
    {
      icon: <BsSliders />,
      title: 'Smart Filters',
      description: '50+ filter options',
      example: 'HOA fees, lot size, year built, and more'
    },
    {
      icon: <BsMap />,
      title: 'Map Drawing',
      description: 'Draw your ideal area',
      example: 'Circle neighborhoods, exclude areas'
    }
  ];

  const searchExamples = [
    {
      category: 'Lifestyle Searches',
      icon: <BsStars />,
      searches: [
        'Homes with stunning mountain views in Scottsdale',
        'Properties with pools near top-rated schools',
        'Horse properties in Cave Creek with 2+ acres',
        'Luxury golf course homes in Grayhawk'
      ]
    },
    {
      category: 'Smart Investment Searches',
      icon: <BsLightning />,
      searches: [
        'Airbnb-ready properties near Old Town',
        'Fixer-uppers under $400K in up-and-coming areas',
        'Multi-family properties with positive cash flow',
        'New construction homes with solar panels'
      ]
    }
  ];

  return (
    <section className="advanced-search-showcase">
      <Container>
        <div className="showcase-header">
          <h2 className="section-title">Ready for Advanced Search?</h2>
          <p className="section-subtitle">
            Our AI understands exactly what you're looking for
          </p>
        </div>

        {/* Search Methods */}
        <div className="search-methods">
          <h3 className="subsection-title">Four Ways to Search</h3>
          <Row>
            {searchMethods.map((method, idx) => (
              <Col key={idx} lg={3} md={6} className="mb-4">
                <Card className="method-card h-100">
                  <Card.Body className="text-center">
                    <div className="method-icon">{method.icon}</div>
                    <h5 className="method-title">{method.title}</h5>
                    <p className="method-description">{method.description}</p>
                    <small className="method-example">"{method.example}"</small>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Search Examples */}
        <div className="search-examples">
          <h3 className="subsection-title">What Can You Search For?</h3>
          <Row>
            {searchExamples.map((category, idx) => (
              <Col key={idx} lg={6} className="mb-4">
                <Card className="example-card">
                  <Card.Body>
                    <div className="example-header">
                      <div className="example-icon">{category.icon}</div>
                      <h5 className="example-title">{category.category}</h5>
                    </div>
                    <ul className="example-list">
                      {category.searches.map((search, searchIdx) => (
                        <li key={searchIdx}>
                          <Button 
                            variant="link" 
                            className="search-link"
                            onClick={() => {
                              localStorage.setItem('chatRealEstateQuery', search);
                              navigate('/search');
                            }}
                          >
                            {search}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* AI Features */}
        <div className="ai-features">
          <Row className="align-items-center">
            <Col lg={6}>
              <h3 className="feature-title">AI That Understands You</h3>
              <ul className="feature-list">
                <li>
                  <strong>Natural Language Understanding:</strong> Tell us what matters - "quiet neighborhood" or "walkable area"
                </li>
                <li>
                  <strong>School District Analysis:</strong> Access to detailed school ratings and enrollment boundaries
                </li>
                <li>
                  <strong>Neighborhood Context:</strong> Crime stats, walkability scores, and local amenities
                </li>
                <li>
                  <strong>Smart Recommendations:</strong> Learn from your searches to suggest better matches
                </li>
              </ul>
            </Col>
            <Col lg={6}>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-number">8,500+</div>
                  <div className="stat-label">Active ARMLS Listings</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">43+</div>
                  <div className="stat-label">AI Search Fields</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">Real-time</div>
                  <div className="stat-label">MLS Updates</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">IDX Compliant</div>
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* CTA */}
        <div className="showcase-cta">
          <Button 
            size="lg"
            className="try-search-btn"
            onClick={() => navigate('/search')}
          >
            Try Advanced Search Now
          </Button>
          <p className="cta-note">
            No sign-up required • Free to search • Real MLS data
          </p>
        </div>
      </Container>
    </section>
  );
};

export default AdvancedSearchShowcase;